import { cn } from "@/lib/utils";
import type { VariantProps } from "class-variance-authority";
import { cva } from "class-variance-authority";
import type { SVGProps } from "react";
import type React from "react";
import ArrowDownIcon from "./icons/arrow-down";
import arrowExternal from "./icons/arrow-external";
import BrainIcon from "./icons/brain";
import CloseIcon from "./icons/close";
import CopyIcon from "./icons/copy";
import DiscordIcon from "./icons/discord";
import DotsLoadingIcon from "./icons/dots-loading";
import ExternalLink from "./icons/external-link";
import GoogleLogo from "./icons/google-logo";
import GroqBadge from "./icons/groq-badge";
import GroqLogo from "./icons/groq-logo";
import InstagramIcon from "./icons/instagram";
import LinkedInIcon from "./icons/linkedin";
import LPUIcon from "./icons/lpu";
import Menu from "./icons/menu";
import Microphone from "./icons/microphone";
import ProgressActivityIcon from "./icons/progress-activity";
import SendIcon from "./icons/send";
import Settings from "./icons/settings";
import SignInIcon from "./icons/sign-in";
import ThreadsIcon from "./icons/threads";
import XIcon from "./icons/x";
import YouTubeIcon from "./icons/youtube";
import ZapIcon from "./icons/zap";

const iconsList: Record<
  string,
  React.ComponentType<SVGProps<SVGSVGElement>>
> = {
  "arrow-down": ArrowDownIcon,
  "dots-loading": DotsLoadingIcon,
  "groq-badge": GroqBadge,
  "groq-logo": GroqLogo,
  "linked-in": LinkedInIcon,
  "progress-activity": ProgressActivityIcon,
  "sign-in": SignInIcon,
  arrowExternal,
  brain: BrainIcon,
  close: CloseIcon,
  copy: CopyIcon,
  discord: DiscordIcon,
  externalLink: ExternalLink,
  google: GoogleLogo,
  instagram: InstagramIcon,
  lpu: LPUIcon,
  menu: Menu,
  microphone: Microphone,
  send: SendIcon,
  settings: Settings,
  threads: ThreadsIcon,
  x: XIcon,
  youtube: YouTubeIcon,
  zap: ZapIcon,
};

export const iconVariants = cva("", {
  variants: {
    color: {
      default: "fill-foreground",
      black: "fill-black",
      muted: "fill-muted",
      primary: "fill-primaryaccent",
      transparent: "fill-transparent",
    },
  },
  defaultVariants: {
    color: "default",
  },
});

const sizeMap = {
  lg: 36,
  md: 24,
  sm: 16,
  default: undefined,
};

export type IconName = keyof typeof iconsList;

type IconProps = SVGProps<SVGSVGElement> &
  VariantProps<typeof iconVariants> & {
    icon: IconName;
    size?: keyof typeof sizeMap;
  };

const Icon = ({
  className,
  color = "default",
  icon,
  size = "default",
  ...props
}: IconProps) => {
  const Comp = iconsList[icon];

  const width = size !== "default" ? sizeMap[size] : undefined;
  const height = size !== "default" ? sizeMap[size] : undefined;

  return (
    // @ts-ignore - IconName type ensures Comp exists
    <Comp
      {...(size !== "default" && { width, height })}
      className={cn(
        iconVariants({
          color,
          className,
        }),
        icon === "progress-activity" && "animate-spin",
      )}
      {...props}
    />
  );
};

export default Icon;
